import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';
import { MapPin, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Percent, Settings, RotateCcw } from 'lucide-react';

// Default Company Targets for market-level metrics
const DEFAULT_THRESHOLDS = {
  cpl: { target: 404.57, max: 484.94, lowerIsBetter: true },
  roas: { target: 6.66, min: 5, lowerIsBetter: false },
  close_rate: { target: 40, min: 30, lowerIsBetter: false },
  nsli: { target: 8000, min: 6000, lowerIsBetter: false },
  com: { target: 15, max: 20, lowerIsBetter: true },
};

// Format helpers
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      return `$${Number(value).toFixed(0)}`;
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'multiple':
      return `${Number(value).toFixed(2)}x`;
    default:
      return Number(value).toFixed(0);
  }
};

// Get status based on thresholds (now accepts custom thresholds)
const getMetricStatus = (actual, metricKey, thresholds) => {
  const threshold = thresholds[metricKey];
  if (!threshold || actual === null || actual === undefined) return 'neutral';
  
  const { target, min, max, lowerIsBetter } = threshold;
  
  if (lowerIsBetter) {
    if (actual <= target) return 'on_pace';
    if (actual <= max) return 'at_risk';
    return 'below_min';
  } else {
    if (actual >= target) return 'on_pace';
    if (actual >= min) return 'at_risk';
    return 'below_min';
  }
};

const statusColors = {
  on_pace: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  at_risk: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  below_min: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

// Settings Modal Component
const MarketSettingsModal = ({ settings, onSettingsChange, onReset }) => {
  const [open, setOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  const handleOpen = (isOpen) => {
    if (isOpen) {
      setLocalSettings(settings);
    }
    setOpen(isOpen);
  };

  const handleSave = () => {
    onSettingsChange(localSettings);
    setOpen(false);
  };

  const handleReset = () => {
    onReset();
    setOpen(false);
  };

  const updateThreshold = (metric, field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [metric]: {
          ...prev.thresholds[metric],
          [field]: value === '' ? DEFAULT_THRESHOLDS[metric][field] : Number(value)
        }
      }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="market-breakdown-settings-btn">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]" data-testid="market-breakdown-settings-modal">
        <DialogHeader>
          <DialogTitle>Market Performance Settings</DialogTitle>
          <DialogDescription>
            Adjust thresholds and display options. Changes are temporary and reset on page refresh.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
          {/* Display Toggles */}
          <div className="space-y-3 border-b pb-4">
            <Label className="text-sm font-medium">Display Options</Label>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-shares" className="text-sm">Show Share Columns</Label>
                <p className="text-xs text-muted-foreground">Spend %, Leads %, Sales %</p>
              </div>
              <Switch
                id="show-shares"
                checked={localSettings.showShares}
                onCheckedChange={(checked) => 
                  setLocalSettings(prev => ({ ...prev, showShares: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-nsli" className="text-sm">Show NSLI Column</Label>
                <p className="text-xs text-muted-foreground">Average sale per market</p>
              </div>
              <Switch
                id="show-nsli"
                checked={localSettings.showNSLI}
                onCheckedChange={(checked) => 
                  setLocalSettings(prev => ({ ...prev, showNSLI: checked }))
                }
              />
            </div>
          </div>

          {/* Threshold Adjustments */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Company Target Overrides</Label>
            <p className="text-xs text-muted-foreground">
              Adjust thresholds for color coding (not saved to database)
            </p>
            
            {/* CPL */}
            <div className="grid grid-cols-3 gap-2 items-center">
              <Label className="text-sm">CPL</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Target</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.cpl.target}
                  onChange={(e) => updateThreshold('cpl', 'target', e.target.value)}
                  className="h-8 text-sm"
                  step="0.01"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Max</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.cpl.max}
                  onChange={(e) => updateThreshold('cpl', 'max', e.target.value)}
                  className="h-8 text-sm"
                  step="0.01"
                />
              </div>
            </div>
            
            {/* ROAS */}
            <div className="grid grid-cols-3 gap-2 items-center">
              <Label className="text-sm">ROAS</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Target</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.roas.target}
                  onChange={(e) => updateThreshold('roas', 'target', e.target.value)}
                  className="h-8 text-sm"
                  step="0.01"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Min</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.roas.min}
                  onChange={(e) => updateThreshold('roas', 'min', e.target.value)}
                  className="h-8 text-sm"
                  step="0.01"
                />
              </div>
            </div>
            
            {/* Close Rate */}
            <div className="grid grid-cols-3 gap-2 items-center">
              <Label className="text-sm">Close Rate</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Target %</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.close_rate.target}
                  onChange={(e) => updateThreshold('close_rate', 'target', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Min %</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.close_rate.min}
                  onChange={(e) => updateThreshold('close_rate', 'min', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            {/* NSLI */}
            <div className="grid grid-cols-3 gap-2 items-center">
              <Label className="text-sm">NSLI</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Target $</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.nsli.target}
                  onChange={(e) => updateThreshold('nsli', 'target', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Min $</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.nsli.min}
                  onChange={(e) => updateThreshold('nsli', 'min', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            {/* COM */}
            <div className="grid grid-cols-3 gap-2 items-center">
              <Label className="text-sm">COM %</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Target %</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.com.target}
                  onChange={(e) => updateThreshold('com', 'target', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Max %</Label>
                <Input
                  type="number"
                  value={localSettings.thresholds.com.max}
                  onChange={(e) => updateThreshold('com', 'max', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-1">
            <RotateCcw className="h-3 w-3" />
            Reset Defaults
          </Button>
          <Button onClick={handleSave}>Apply Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Default settings
const DEFAULT_SETTINGS = {
  showShares: true,
  showNSLI: true,
  thresholds: { ...DEFAULT_THRESHOLDS }
};

// Market row component
const MarketRow = ({ market, companyTotals, isCompanyRow = false, settings }) => {
  const kpis = market.kpis || {};
  const thresholds = settings.thresholds;
  
  // Calculate share percentages
  const spendShare = companyTotals.spend > 0 
    ? ((kpis.spend?.actual || 0) / companyTotals.spend) * 100 
    : 0;
  const leadsShare = companyTotals.leads > 0 
    ? ((kpis.leads?.actual || 0) / companyTotals.leads) * 100 
    : 0;
  const salesShare = companyTotals.sales > 0 
    ? ((kpis.sales?.actual || 0) / companyTotals.sales) * 100 
    : 0;
  
  // Calculate derived metrics
  const closeRate = kpis.close_rate?.actual;
  const nsli = kpis.sales?.actual && kpis.revenue?.actual 
    ? kpis.revenue.actual / kpis.sales.actual 
    : null;
  const com = kpis.spend?.actual && kpis.revenue?.actual
    ? (kpis.spend.actual / kpis.revenue.actual) * 100
    : null;

  const metrics = [
    ...(settings.showShares ? [
      { key: 'spend_share', value: spendShare, format: 'percent', label: 'Spend %' },
      { key: 'leads_share', value: leadsShare, format: 'percent', label: 'Leads %' },
      { key: 'sales_share', value: salesShare, format: 'percent', label: 'Sales %' },
    ] : []),
    { key: 'cpl', value: kpis.cpl?.actual, format: 'currency', label: 'CPL' },
    { key: 'roas', value: kpis.roas?.actual, format: 'multiple', label: 'ROAS' },
    { key: 'close_rate', value: closeRate, format: 'percent', label: 'Close %' },
    ...(settings.showNSLI ? [
      { key: 'nsli', value: nsli, format: 'currency', label: 'NSLI' },
    ] : []),
    { key: 'com', value: com, format: 'percent', label: 'COM %' },
  ];

  return (
    <tr className={`border-b dark:border-slate-700 ${isCompanyRow ? 'bg-slate-100 dark:bg-slate-800 font-semibold' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
      <td className="px-3 py-3 text-sm font-medium dark:text-white whitespace-nowrap sticky left-0 bg-inherit">
        <div className="flex items-center gap-2">
          <MapPin className={`h-4 w-4 ${isCompanyRow ? 'text-primary' : 'text-slate-400'}`} />
          {market.market_name}
        </div>
      </td>
      {metrics.map(({ key, value, format }) => {
        const status = ['cpl', 'roas', 'close_rate', 'nsli', 'com'].includes(key) 
          ? getMetricStatus(value, key, thresholds) 
          : 'neutral';
        
        return (
          <td 
            key={key} 
            className={`px-3 py-2 text-center text-sm ${
              ['cpl', 'roas', 'close_rate', 'nsli', 'com'].includes(key) 
                ? statusColors[status] 
                : ''
            }`}
          >
            {formatValue(value, format)}
          </td>
        );
      })}
    </tr>
  );
};

// Main Market Performance Breakdown component
export const MarketPerformanceBreakdown = ({ data, loading }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  if (loading) {
    return (
      <Card className="dark:bg-slate-800/60" data-testid="market-breakdown-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Market Performance Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const byMarket = data?.by_market || [];
  const companyKpis = data?.kpis || {};
  
  const companyTotals = {
    spend: companyKpis.spend?.actual || 0,
    leads: companyKpis.leads?.actual || 0,
    sales: companyKpis.sales?.actual || 0,
  };
  
  const companyRow = {
    market_id: 'company',
    market_name: 'COMPANY TOTAL',
    kpis: companyKpis,
  };

  // Dynamic columns based on settings
  const columns = [
    { key: 'market_name', label: 'Market', width: 'w-32' },
    ...(settings.showShares ? [
      { key: 'spend_share', label: 'Spend %' },
      { key: 'leads_share', label: 'Leads %' },
      { key: 'sales_share', label: 'Sales %' },
    ] : []),
    { key: 'cpl', label: 'CPL', hasThreshold: true },
    { key: 'roas', label: 'ROAS', hasThreshold: true },
    { key: 'close_rate', label: 'Close %', hasThreshold: true },
    ...(settings.showNSLI ? [
      { key: 'nsli', label: 'NSLI', hasThreshold: true },
    ] : []),
    { key: 'com', label: 'COM %', hasThreshold: true },
  ];

  // Check if using custom thresholds
  const hasCustomThresholds = JSON.stringify(settings.thresholds) !== JSON.stringify(DEFAULT_THRESHOLDS);

  return (
    <Card className="dark:bg-slate-800/60" data-testid="market-performance-breakdown">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Market Performance Breakdown
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" /> On Track
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500" /> At Risk
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" /> Below Min
              </span>
            </div>
            <MarketSettingsModal 
              settings={settings} 
              onSettingsChange={setSettings}
              onReset={handleReset}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Per-market share and efficiency metrics with company targets
          {hasCustomThresholds && (
            <span className="ml-2 text-orange-500">(using custom thresholds)</span>
          )}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                {columns.map(col => (
                  <th 
                    key={col.key} 
                    className={`px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide ${col.width || ''} ${col.key === 'market_name' ? 'text-left sticky left-0 bg-slate-50 dark:bg-slate-900/50' : 'text-center'}`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {col.label}
                      {col.hasThreshold && (
                        <span className="text-primary" title="Has company target">•</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <MarketRow 
                market={companyRow} 
                companyTotals={companyTotals} 
                isCompanyRow={true}
                settings={settings}
              />
              
              {byMarket.map(market => (
                <MarketRow 
                  key={market.market_id} 
                  market={market} 
                  companyTotals={companyTotals}
                  settings={settings}
                />
              ))}
              
              {byMarket.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-8 text-center text-muted-foreground">
                    No market data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Threshold Legend */}
        <div className="p-4 border-t dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30">
          <div className="text-xs text-muted-foreground">
            <strong className="text-slate-700 dark:text-slate-300">Company Targets:</strong>
            <span className="ml-4">CPL: Target ${settings.thresholds.cpl.target}, Max ${settings.thresholds.cpl.max}</span>
            <span className="ml-4">ROAS: Target {settings.thresholds.roas.target}x, Min {settings.thresholds.roas.min}x</span>
            <span className="ml-4">Close: Target {settings.thresholds.close_rate.target}%, Min {settings.thresholds.close_rate.min}%</span>
            {settings.showNSLI && (
              <span className="ml-4">NSLI: Target ${settings.thresholds.nsli.target}</span>
            )}
            <span className="ml-4">COM: Target {settings.thresholds.com.target}%, Max {settings.thresholds.com.max}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketPerformanceBreakdown;
